// src/components/auth/MfaSetupInline.tsx
// Inline MFA setup component for use in registration, invite, and login flows.

import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, CheckCircle, Copy } from 'lucide-react';
import { authApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import toast from 'react-hot-toast';

interface MfaSetupInlineProps {
  onComplete: () => void;
  /** Label for the final button. Defaults to the sign-up/onboarding wording. */
  completeLabel?: string;
}

const MfaSetupInline: React.FC<MfaSetupInlineProps> = ({ onComplete, completeLabel = 'Continue to Zamino' }) => {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState<'qr' | 'verify' | 'done'>('qr');
  const [setupData, setSetupData] = useState<{ secret: string; qrDataUrl: string } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initError, setInitError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const begin = async () => {
      try {
        setIsLoading(true);
        const data = await authApi.mfaBeginSetup();
        if (!cancelled) {
          setSetupData({ secret: data.secret, qrDataUrl: data.qrDataUrl });
        }
      } catch {
        if (!cancelled) setInitError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    begin();
    return () => { cancelled = true; };
  }, []);

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (totpCode.length !== 6) return;
    try {
      setIsLoading(true);
      await authApi.mfaVerifySetup(totpCode);
      // Refresh the cached user so mfaEnabled flips to true across the app.
      await refreshUser();
      setStep('done');
      toast.success('Two-factor authentication enabled!');
    } catch {
      toast.error('Invalid code. Please try again.');
      setTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      toast.success('Secret key copied to clipboard');
    }
  };

  if (initError) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm text-destructive">
          Failed to start MFA setup. Please try again later.
        </p>
      </div>
    );
  }

  if (isLoading && !setupData) {
    return (
      <div className="space-y-4 text-center py-6">
        <Smartphone className="w-10 h-10 text-primary mx-auto animate-pulse" />
        <p className="text-sm text-muted-foreground">Preparing authenticator setup...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto w-12 h-12 bg-accent rounded-full flex items-center justify-center">
          <Shield className="w-6 h-6 text-primary" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-foreground">
          Set Up Two-Factor Authentication
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Secure your account with an authenticator app
        </p>
      </div>

      {/* Step: QR — scan QR code */}
      {step === 'qr' && setupData && (
        <div className="space-y-5">
          <div className="p-3 bg-info/15 border border-info/20 rounded-lg">
            <p className="text-sm text-info">
              <strong>Step 1:</strong> Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
          </div>

          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-lg">
              <img src={setupData.qrDataUrl} alt="MFA QR Code" className="w-48 h-48" />
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-2">
              Can't scan? Enter this key manually:
            </p>
            <div className="inline-flex items-center gap-2 bg-muted px-4 py-2 rounded-lg">
              <code className="text-sm font-mono text-foreground select-all">
                {setupData.secret}
              </code>
              <button onClick={copySecret} className="text-muted-foreground hover:text-foreground">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <Button onClick={() => setStep('verify')} className="w-full">
            I've scanned the QR code — Next
          </Button>
        </div>
      )}

      {/* Step: Verify — enter TOTP code */}
      {step === 'verify' && (
        <form onSubmit={verifyCode} className="space-y-5">
          <div className="p-3 bg-info/15 border border-info/20 rounded-lg">
            <p className="text-sm text-info">
              <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Verification Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full text-center text-2xl tracking-[0.5em] font-mono px-4 py-3 border border-input rounded-lg bg-card text-foreground focus:ring-2 focus:ring-ring focus:border-primary"
              placeholder="000000"
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setStep('qr'); setTotpCode(''); }}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              type="submit"
              className="flex-1"
              isLoading={isLoading}
              disabled={totpCode.length !== 6}
            >
              Verify & Enable
            </Button>
          </div>
        </form>
      )}

      {/* Step: Done */}
      {step === 'done' && (
        <div className="space-y-5">
          <div className="p-4 bg-success/15 border border-success/20 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              <p className="text-sm font-medium text-success">
                Two-factor authentication is active
              </p>
            </div>
            <p className="text-sm text-success mt-1">
              Your account is now secured. You'll need your authenticator app each time you sign in.
            </p>
          </div>

          <Button onClick={onComplete} className="w-full" size="lg">
            {completeLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MfaSetupInline;
